import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge, Dialog } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useProfileContext } from "context";
import { useGetGoogleReviewsStatus, useInitiateGoogleOAuth } from "hooks";
import { PermissionAlertDialog } from "components";

export function BrowseGoogleReviewsCard({ userProfile }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showGoogleLoginDialog, setShowGoogleLoginDialog] = useState(false);

  const { data: googleReviewsStatusData, isLoading: isStatusLoading } = useGetGoogleReviewsStatus(
    userProfile.organisation_id
  );
  
  const initiateOAuth = useInitiateGoogleOAuth();

  const isGoogleReviewsActive = () => {
    if (isStatusLoading) return false;
    return googleReviewsStatusData?.data?.status === "active";
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";

    if (!isOwner) {
      setShowPermissionAlert(true);
      return;
    }

    // Show the Google login information dialog
    setShowGoogleLoginDialog(true);
  };

  const handleContinueToGoogle = async () => {
    setShowGoogleLoginDialog(false);
    setIsLoading(true);

    try {
      // Get the OAuth URL from backend
      const result = await initiateOAuth.mutateAsync({
        organisation_id: userProfile.organisation_id,
        redirect_uri: `${window.location.origin}/integrations/google-reviews/callback`,
      });

      if (result?.data?.oauth_url) {
        // Store organisation_id in sessionStorage for callback
        sessionStorage.setItem("google_reviews_org_id", userProfile.organisation_id);
        sessionStorage.setItem("google_reviews_state", result.data.state);
        
        // Redirect to Google OAuth
        window.location.href = result.data.oauth_url;
      } else {
        console.error("No OAuth URL received");
      }
    } catch (error) {
      console.error("Failed to initiate OAuth:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageClick = () => {
    navigate("/google-reviews-setup", {
      state: {
        orgId: userProfile.organisation_id,
        isManaging: true,
      },
    });
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          width: "100%",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
            height="40px"
            width="40px"
            p="2"
            justify="center"
            align="center"
          >
            <img
              src={require("../../../assets/images/google.png")}
              alt="Google Reviews"
              style={{ width: "24px" }}
            />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              Google Reviews
            </Text>
            <Text style={{ color: "var(--gray-a11)" }} size="1">
              Customer reviews
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isGoogleReviewsActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button
              onClick={handleConnectClick}
              size="2"
              variant="soft"
              color="gray"
              loading={isLoading}
            >
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />

      {/* Google Login Information Dialog */}
      <Dialog.Root open={showGoogleLoginDialog} onOpenChange={setShowGoogleLoginDialog}>
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Connect Google Business Profile</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            To sync your Google reviews, you'll need to sign in with a Google account that has access to a Google Business Profile managing your locations.
          </Dialog.Description>
          
          <Flex direction="column" gap="3" mb="4">
            <Text size="2" color="gray">
              Please ensure:
            </Text>
            <Flex direction="column" gap="2" style={{ paddingLeft: "var(--space-3)" }}>
              <Text size="2">• You have a Google Business Profile set up</Text>
              <Text size="2">• Your business locations are added to the profile</Text>
              <Text size="2">• You have owner or manager access to the profile</Text>
            </Flex>
          </Flex>

          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleContinueToGoogle}>
              Continue
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
